import { ChangeDetectionStrategy, Component, booleanAttribute, input, output } from '@angular/core';
import { MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { Loader } from '@shared/components/loader/loader';

/**
 * - `sm` — confirmations and short questions.
 * - `md` (default) — short forms.
 * - `lg` — tabular or list content.
 * - `full` — edge-to-edge; for content that does not belong in a box at all.
 *
 * Every size collapses to `full` below the tablet breakpoint, so call sites
 * never branch on viewport width.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'full';

/**
 * The action row at the bottom of a modal. Project one into `sci-modal`:
 *
 * ```html
 * <sci-modal [title]="title">
 *   …
 *   <sci-modal-footer>
 *     <button sci-button variant="link" color="neutral" (click)="close()">Cancel</button>
 *     <button sci-button (click)="save()">Save</button>
 *   </sci-modal-footer>
 * </sci-modal>
 * ```
 *
 * It is a component rather than a plain wrapper the modal styles from outside,
 * because emulated encapsulation puts projected content out of `sci-modal`'s
 * reach: a `gap` declared there applies between the modal's own children, not
 * between the buttons inside the projected node. Owning the row means the
 * spacing between actions is the kit's job, not each call site's.
 *
 * Omit it entirely and the modal renders no footer chrome at all.
 */
@Component({
  selector: 'sci-modal-footer',
  template: '<ng-content />',
  styleUrl: './modal-footer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalFooter {}

/**
 * The frame every modal in the app is built from: header with title and close
 * affordance, scrollable body, footer for actions.
 *
 * It knows nothing about `MatDialog` — it is the box, not the overlay — so it
 * renders just as well inline (the UI Kit page shows it that way). Features do
 * not open it directly; `ModalService` does, wrapping a component that uses
 * this as its root element:
 *
 * ```html
 * <sci-modal [title]="'views.crew.editMember' | translate" size="lg" (dismissed)="close()">
 *   <form>…</form>
 *   <sci-modal-footer>
 *     <button sci-button variant="link" color="neutral" (click)="close()">Cancel</button>
 *     <button sci-button [loading]="saving()" (click)="save()">Save</button>
 *   </sci-modal-footer>
 * </sci-modal>
 * ```
 *
 * `Modal` and `ModalFooter` are separate imports — the footer is projected, not
 * part of this component's template.
 */
@Component({
  selector: 'sci-modal',
  imports: [MatDialogTitle, MatIconModule, TranslatePipe, Loader],
  templateUrl: './modal.html',
  styleUrl: './modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.modal--sm]': 'size() === "sm"',
    '[class.modal--lg]': 'size() === "lg"',
    '[class.modal--full]': 'size() === "full"',
  },
})
export class Modal {
  /** Final display text — translate at the call site. Wired to `aria-labelledby`. */
  readonly title = input.required<string>();
  readonly subtitle = input<string>();
  readonly size = input<ModalSize>('md');

  /**
   * Shows the ✕ in the header. Turning it off does not by itself make the modal
   * unescapable — that is `disableClose` on the dialog config, and the two are
   * usually set together.
   */
  readonly closable = input(true, { transform: booleanAttribute });

  /**
   * Covers the whole frame — actions included — while an operation is in
   * flight, so the footer buttons can't be pressed twice. Replaces the old
   * app's `ng-block-ui`.
   */
  readonly loading = input(false, { transform: booleanAttribute });

  /**
   * The ✕ was pressed. The modal does not close itself: the component that owns
   * the `MatDialogRef` decides what closing means and what result it carries.
   */
  readonly dismissed = output<void>();
}
